import { Filter, LogGroup, Scale, Size } from "../.././MyHelpers";
import { ResourceIdentifier } from "../../ResourceManager/ResourceIdentifier";
import { IReadOnlyResourceManager, IWriteOnlyResourceManager } from "../../ResourceManager/ResourceManager";
import { Spine } from "pixi-spine";
import * as PIXI from 'pixi.js';
import { AwardOptionPosition, AwardType, BetMultiplierAwardOption, CreditAwardOption, IAwardOption, IFeatureResult, JackpotAwardOption, JackpotLevel, PlayWindow, ScatterRandomAwardConfiguration, SlotGameBet } from "../../api";
import { assertEqual, assertNotLess, assertNotNullish } from "../../debug";
import { JackpotProperties } from "../../JackpotProperties";
import { Money } from "../../Money";
import { Settings } from "../../Settings";
import { MyCanvas } from "../MyCanvas";
import { SlotFeature } from "../SlotFeature";
import { SlotSymbol } from "../SlotSymbol";
import { IRewardOptionFactory, RewardColor, RewardOptionFactory, RewardOptionParams } from "./RewardOptionFactory";

export interface ScatterRandomAwardCreateParams
{
    readonly logGroup: LogGroup;
	readonly resid: ResourceIdentifier;
    // readonly reelSet: number[][];
	readonly availableSymbols: SlotSymbol[];
}

export interface ScatterRandomAwardPrepareParams
{
    readonly reelSet: number[][];
}

export interface IRewardOption
{
    Name(): string;
    PrepareResources(resourceManager: IWriteOnlyResourceManager): void;
    ResourcesLoaded(resourceManager: IReadOnlyResourceManager): void;
    UpdateBet(bet: SlotGameBet);
    DrawTo(canvas: MyCanvas);
    DrawToPIXI(canvas: PIXI.AnimatedSprite , symbolsSize?: Size , strValueMultiplier? : string , jackpotMultiplier?: number  , awardShow? : boolean);    
    DrawToSpine(spine: Spine , symbolsSize?: Size , strValueMultiplier? : string , jackpotMultiplier?: number , awardShow? : boolean );    
    TextToDraw();
    Update();
    MultiplierAward(bonusMultiplier : number) : string 
}

export interface ScatterRandomAwardOptionResult
{
    readonly AwardType: AwardType;
    readonly AwardName: string;
}

export interface ScatterRandomAwardCashOptionResult extends ScatterRandomAwardOptionResult
{
    readonly BetMultiplier: number;
}

export interface ScatterRanomAwardJackpotOptionResult extends ScatterRandomAwardOptionResult
{
    readonly JackpotName: JackpotLevel;
}

export interface ScatterRandomAwardPosition
{
    readonly Row: number,
    readonly Col: number,
    readonly Reel: number,
    readonly ReelOffset: number,
    readonly ReelPosition: number,
    readonly AwardOption: ScatterRandomAwardOptionResult
}

export interface ScatterRandomAwardAwardPosition
{
    readonly AwardOptionPosition: ScatterRandomAwardPosition;
    readonly Value: Money;
}

export interface ScatterRandomAwardResults
{
    readonly FeatureTriggered: boolean;
    readonly ScattersAddedThisSpin: number;
    readonly Count: number;
    readonly ScattersTotal: number;
    readonly Scatters: ScatterRandomAwardOptionResult[][];
    readonly ScattersPosition: ScatterRandomAwardPosition[];
    readonly ScattersPositionThisSpin: ScatterRandomAwardPosition[];

	readonly Awards: ScatterRandomAwardAwardPosition[];
    readonly FullAward: {
		readonly AwardOption: ScatterRandomAwardOptionResult,
		readonly Value: Money
	};
}

enum RTRMapType
{
    Initial = "Initial",
    Random = "Random",
    Backend = "Backend",
}

interface RTRMap
{
    type: RTRMapType;
    rewardOptionIndex: number;
}

export interface SRAHistoryParams
{
    bet: SlotGameBet;
    scatterPosition: AwardOptionPosition;
    parentElement: HTMLElement;
    multiplier: number;
}

export interface SRAHistoryParamsCanvas
{
    bet: SlotGameBet;
    scatterPosition: AwardOptionPosition;
    parentElement: MyCanvas;
}

export interface SRAUpdateReelsParams
{
    playWindow: PlayWindow;
    reelSet: number[][];
}

interface SRACommonConfig
{
    MadeForBetCredits: number;
    RequiredSymbols: string[];
    AvailableAwards: IAwardOption[];
}

export class ScatterRandomAward extends SlotFeature
{

    //protected logGroup: LogGroup;
	//protected resid: ResourceIdentifier;

    // protected config: ScatterRandomAwardConfiguration
    
    protected config: SRACommonConfig;
	
	protected view: HTMLElement;

    protected requiredSymbols: SlotSymbol[];
    protected requiredSymbolsIndex: number[];

    protected rewardOption: IRewardOption[] = [];
    protected rewardOptionMap: Map<string, IRewardOption> = new Map<string, IRewardOption>();

    protected rewardOptionFactory: IRewardOptionFactory = new RewardOptionFactory();

    protected availableRewardsPerReel: number[][];

    protected reelToRewardMap: Map<number, RTRMap>[] = [];

    protected color: RewardColor = null;



    public constructor(params: ScatterRandomAwardCreateParams, config: Record<string, ScatterRandomAwardConfiguration>, color: RewardColor) 
    {
        super(params.logGroup, params.resid, "ScatterRandomAward", null)

        // TODO create Common configuration
        const configList = Object.values(config);
        assertNotLess(1, configList.length, "ScatterRandomAward config must have at least one configuration");

        const requiredSymbols = configList.map(c => c.RequiredSymbols).flat().filter(Filter.UniqueItem);
        const availableAwards = configList.map(c => c.AvailableAwards).flat().filter(Filter.UniqueBy((ao => ao.AwardName)));
        const madeForBetCredits = configList[0].MadeForBetCredits;

        this.config = {
            MadeForBetCredits: madeForBetCredits,
            RequiredSymbols: requiredSymbols,
            AvailableAwards: availableAwards,
        };


        //this.logGroup = params.logGroup.Add("ScatterRandomAward");
        //this.resid = params.resid.Add("ScatterRandomAward");
        assertNotNullish(requiredSymbols, "ScatterRandomAward config must have required symbols");
        this.requiredSymbols = requiredSymbols.map(s => params.availableSymbols.find(s2 => s2.ID == s));

        if (this.requiredSymbols.some(s => !s))
            throw `Missing some required symbols ${requiredSymbols.join(", ")}`;

        this.requiredSymbolsIndex = this.requiredSymbols.map(s => s.Index);

        let optionParams: RewardOptionParams = {
            logGroup: this.logGroup,
            resid: this.resid,
        };
        
        this.color = color;
        

		availableAwards.forEach(config => 
		{
			let option: IRewardOption = this.rewardOptionFactory.CreateRewardOption(optionParams, config, this.color); 
			this.rewardOption.push(option);
		});

        this.rewardOption.forEach(ro => this.rewardOptionMap.set(ro.Name(), ro));
    };

    updateReelsParams: SRAUpdateReelsParams = null;
    public UpdateReels(p: SRAUpdateReelsParams): void
    {
        this.updateReelsParams = p;

        this.reelToRewardMap?.forEach(m => m.clear());
        this.reelToRewardMap = [];

        // find positions on reels where we need to display the random award
        // just use normal distribution
        for (let r = 0; r < p.reelSet.length; r++)
        {
            let rewardMap = new Map<number, RTRMap>();
            for (let s = 0; s < p.reelSet[r].length; s++)
            {
                if (this.requiredSymbolsIndex.includes(p.reelSet[r][s]))
                {
                    rewardMap.set(s, {
                        rewardOptionIndex: 0,
                        type: RTRMapType.Initial
                    });
                }
            }

            this.reelToRewardMap.push(rewardMap);
        }

        this.GenerateAwards();
    }

    public override UpdateBet(bet: SlotGameBet): void
    {
        for (let i = 0; i < this.rewardOption.length; i++)
        {
            this.rewardOption[i].UpdateBet(bet);
        }
    }

    public async PrepareResources(resourceManager: IWriteOnlyResourceManager): Promise<void>
    {
        for (let i = 0; i < this.rewardOption.length; i++)
        {
            await this.rewardOption[i].PrepareResources(resourceManager);
        }
    }

    public async ResourcesLoaded(resourceManager: IReadOnlyResourceManager): Promise<void>
    {
        for (let i = 0; i < this.rewardOption.length; i++)
        {
            await this.rewardOption[i].ResourcesLoaded(resourceManager);
        }
    }

    public GetAwardOptions(): IRewardOption[]
    {
        return this.rewardOption;
    }

    protected GenerateAwards()
    {
        // assertNotNullish(this.updateReelsParams, "Reels need to be set");
        
        for (let r = 0; r < this.reelToRewardMap.length; r++)
        {
            this.reelToRewardMap[r].forEach((v, k) => {
                this.reelToRewardMap[r].set(k, {
                    rewardOptionIndex: this.rewardOption.randomIndex(),
                    type: RTRMapType.Random
                });
            });
        }

        //// Log.Debug("SRADEBUG", "Generaing awards");
        //console.log("SRADEBUG", "Generating awards", this.reelToRewardMap);
    }

    

    public Start(): void
    {
        // generate random awards
        this.GenerateAwards();
    }

    protected result: ScatterRandomAwardPosition[];
    public SetAwards(/*result: ScatterRandomAwardResults*/result: ScatterRandomAwardPosition[]): void
    {
        assertNotNullish(result, "ScatterRandomAward.SetAwards result can't be null");

        result.forEach(p => {
            let optionIndex: number = this.rewardOption.findIndex(o => o.Name() == p.AwardOption.AwardName);
            if (optionIndex == -1) throw `Option "${p.AwardOption.AwardName} not loaded`;

            this.reelToRewardMap[p.Reel].set(p.ReelPosition, {
                rewardOptionIndex: optionIndex,
                type: RTRMapType.Backend
            });
        });
    }

    // TODO use symbol name vs symbol index;
    public Draw(reelIndex: number, reelPosition: number, rowIndex: number, symbol: SlotSymbol, canvas?: MyCanvas, canvasPIXI?: MyCanvas)
    {

        if (this.requiredSymbolsIndex.includes(symbol.Index)) 
        {
            //// Log.Debug(this.logGroup, `Drawing to symbol ${symbol.id} index ${symbol.index}`);

            let m = this.reelToRewardMap[reelIndex].get(reelPosition);
            let rewardOptionIndex: number = m.rewardOptionIndex;

            let reward = this.rewardOption[rewardOptionIndex];
            //// Log.Debug("SRADEBUG", `Drawing rewardOptionIndex on reelIndex ${reelIndex} rowIndex ${rowIndex} reelPosition ${reelPosition}, rewardOptionIndex ${rewardOptionIndex} name ${reward.Name()} draw ${reward.TextToDraw()}`);
            //console.log(reward);

            this.rewardOption[rewardOptionIndex].DrawTo(canvas);

            // canvas.context.save();

            // canvas.context.font = '10px sans-serif';
            // canvas.context.fillStyle = "#ff0000"
            // canvas.context.fillText(`${reward.TextToDraw()} - ${m.type} `, 10, 10);

            // canvas.context.restore();
        }       
    }

    public RequiredSymbols(): SlotSymbol[]
    {
        return this.requiredSymbols;
	}
	
	public View(): HTMLElement
	{
		return null;
	}

	public Update(): void
	{
        for (let i = 0; i < this.rewardOption.length; i++)
        {
            this.rewardOption[i].Update();
        }
	}

	public get IsRunning(): boolean
	{
		return false;
	}

	public RequestStop(): void
	{

	}

	public Prepare(featureResult: IFeatureResult): void
	{
        assertEqual(featureResult.FeatureName, this.featureName);


	}

    public GetHistoryValue(historyParams: SRAHistoryParams)
    {
        let factory: Record<AwardType, (historyParams: SRAHistoryParams) => void> = {
            [AwardType.BetMultiplier]: (historyParams) => this.GetHistoryBetMultiplierValue(historyParams),
            [AwardType.Credit]: (historyParams) => this.GetHistoryCreditValue(historyParams),
            [AwardType.Jackpot]: (historyParams) => this.GetHistoryJackpotValue(historyParams),
        }

        const awardType = historyParams.scatterPosition.AwardOption.AwardType;
        const factoryFunc = factory[awardType];

        factoryFunc(historyParams);
    }

    protected GetHistoryBetMultiplierValue(historyParams: SRAHistoryParams)
    {
        assertEqual(historyParams.scatterPosition.AwardOption.AwardType, AwardType.BetMultiplier);

        const betMultiplierAwardOption: BetMultiplierAwardOption = historyParams.scatterPosition.AwardOption as BetMultiplierAwardOption;
        const baseSpinBetCredits: number = historyParams.bet.TotalBetCredits;
        const winCredits = betMultiplierAwardOption.BetMultiplier * baseSpinBetCredits;

        historyParams.parentElement.innerText = winCredits.toString();
    }

    protected GetHistoryCreditValue(historyParams: SRAHistoryParams)
    {

        assertEqual(historyParams.scatterPosition.AwardOption.AwardType, AwardType.Credit);

        const multiplier = historyParams.multiplier ?? 1;

        const creditAwardOption: CreditAwardOption = historyParams.scatterPosition.AwardOption as CreditAwardOption;
        //let baseSpinBet: Money = gameState.Bet.TotalBetCents;
        const betCredits = historyParams.bet.TotalBetCredits;
        const madeForBetCredits = this.config.MadeForBetCredits;

        const factor = Math.round(betCredits / madeForBetCredits);
        const winCredits = factor * creditAwardOption.Credits * multiplier;

        historyParams.parentElement.innerText = winCredits.toString();
    }

    protected GetHistoryJackpotValue(historyParams: SRAHistoryParams)
    {
        assertEqual(historyParams.scatterPosition.AwardOption.AwardType, AwardType.Jackpot);

        let multiplier = historyParams.multiplier ?? 1;

        const jackpotAwardOption: JackpotAwardOption = historyParams.scatterPosition.AwardOption as JackpotAwardOption;

        const jackpotLevel: JackpotLevel = jackpotAwardOption.JackpotLevel;
        
        // only jackpot level 1 and 2 can be multiplied
        if (jackpotLevel != JackpotLevel.Jp1 && jackpotLevel != JackpotLevel.Jp2)
        {
            multiplier = 1;
        }

        const jackpotProperties = JackpotProperties[Settings.JackpotDisplayType][jackpotLevel];
        
        historyParams.parentElement.style.color = jackpotProperties.color;

        if (multiplier > 1)
        {
            historyParams.parentElement.innerHTML = `${jackpotProperties.name}<br />x${multiplier})`;
        }
        else
        {
            historyParams.parentElement.innerText = jackpotProperties.name;
        }
        
    }

    public GetHistoryValueCanvas(historyParams: SRAHistoryParamsCanvas)
    {
        let factory: Record<AwardType, (historyParams: SRAHistoryParamsCanvas) => void> = {
            [AwardType.BetMultiplier]: (historyParams) => this.GetHistoryBetMultiplierValueCanvas(historyParams),
            [AwardType.Credit]: (historyParams) => this.GetHistoryCreditValueCanvas(historyParams),
            [AwardType.Jackpot]: (historyParams) => this.GetHistoryJackpotValueCanvas(historyParams),
        }

        const awardType = historyParams.scatterPosition.AwardOption.AwardType;
        const factoryFunc = factory[awardType];

        factoryFunc(historyParams);
    }

    protected GetHistoryBetMultiplierValueCanvas(historyParams: SRAHistoryParamsCanvas)
    {
        assertEqual(historyParams.scatterPosition.AwardOption.AwardType, AwardType.BetMultiplier);

        const betMultiplierAwardOption: BetMultiplierAwardOption = historyParams.scatterPosition.AwardOption as BetMultiplierAwardOption;
        const baseSpinBetCredits: number = historyParams.bet.TotalBetCredits;
        const winCredits = betMultiplierAwardOption.BetMultiplier * baseSpinBetCredits;

        this.SetCanvasHistory(winCredits.toString(), historyParams.parentElement, historyParams.parentElement.rect.height / 6, "white");
    }

    protected GetHistoryCreditValueCanvas(historyParams: SRAHistoryParamsCanvas)
    {

        assertEqual(historyParams.scatterPosition.AwardOption.AwardType, AwardType.Credit);

        const creditAwardOption: CreditAwardOption = historyParams.scatterPosition.AwardOption as CreditAwardOption;
        //let baseSpinBet: Money = gameState.Bet.TotalBetCents;
        const betCredits = historyParams.bet.TotalBetCredits;
        const madeForBetCredits = this.config.MadeForBetCredits;

        const factor = Math.floor((betCredits / madeForBetCredits) + 0.5);
        const winCredits = factor * creditAwardOption.Credits;

        this.SetCanvasHistory(winCredits.toString(), historyParams.parentElement, historyParams.parentElement.rect.height / 6, "white");
    }

    protected GetHistoryJackpotValueCanvas(historyParams: SRAHistoryParamsCanvas)
    {
        assertEqual(historyParams.scatterPosition.AwardOption.AwardType, AwardType.Jackpot);
        const jackpotAwardOption: JackpotAwardOption = historyParams.scatterPosition.AwardOption as JackpotAwardOption;

        const jackpotLevel: JackpotLevel = jackpotAwardOption.JackpotLevel;
        const jackpotProperties = JackpotProperties[Settings.JackpotDisplayType][jackpotLevel];
        
        this.SetCanvasHistory(jackpotProperties.name, historyParams.parentElement, historyParams.parentElement.rect.height / 6, jackpotProperties.color);
    }

    protected SetCanvasHistory(text: string, Canvas: MyCanvas, fontSize: number, colorfont: string) {
		Canvas.context.fillStyle = colorfont;
					
		Canvas.context.font = "bold " + fontSize + "px " + "NotoSansDisplay-Bold";
							
		//Canvas.context.clearRect(0, 0, (Canvas.rect.width), (Canvas.rect.height));
		Canvas.context.textBaseline = "middle";
		Canvas.context.textAlign="center";							
		Canvas.context.lineJoin = "round"; //Experiment with "bevel" & "round" for the effect you want!
		Canvas.context.miterLimit = 2;
		Canvas.context.shadowBlur = 5;		
		Canvas.context.strokeStyle = "black";
		Canvas.context.lineWidth = Scale.fh(10);
		Canvas.context.strokeText(text, Canvas.rect.width/2 , Canvas.rect.height/2 );
		
		Canvas.context.fillText(text, Canvas.rect.width/2 , Canvas.rect.height/2 );
		colorfont = null;
		fontSize = null;
		text = null;
		Canvas = null;
	}
}
